import React, { useEffect, useMemo, useState } from 'react';
import { aiAutomationApi } from '../../utils/aiAutomationApi';

const HIDDEN_TEMPLATE_KEYS = new Set([
  'interview_invitation',
  'interview_confirmation',
  'ai_interview_confirmation',
]);

const hasHtmlContent = (template) => /<\/?[a-z][\s\S]*>/i.test(String(template?.body || ''));

const getVisibleTemplates = (items = []) => items.filter((template) => (
  !HIDDEN_TEMPLATE_KEYS.has(template?.template_key) && !hasHtmlContent(template)
));

export default function AdminMailTemplatesPage() {
  const [companyId, setCompanyId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [form, setForm] = useState({ name: '', description: '', subject: '', body: '', variables: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const selected = useMemo(
    () => templates.find((template) => template.template_key === selectedKey) || null,
    [templates, selectedKey],
  );

  const loadTemplates = async () => {
    setLoading(true);
    setMessage('');
    try {
      const query = companyId.trim() ? `?cid=${encodeURIComponent(companyId.trim())}` : '';
      const response = await aiAutomationApi.get(`/admin/mail-templates${query}`);
      const nextTemplates = getVisibleTemplates(response?.templates || []);
      setTemplates(nextTemplates);
      const nextSelected = selectedKey && nextTemplates.some((item) => item.template_key === selectedKey)
        ? selectedKey
        : nextTemplates[0]?.template_key || '';
      setSelectedKey(nextSelected);
      const active = nextTemplates.find((item) => item.template_key === nextSelected) || nextTemplates[0];
      if (active) {
        setForm({
          name: active.name || '',
          description: active.description || '',
          subject: active.subject || '',
          body: active.body || '',
          variables: active.variables || [],
        });
      }
    } catch (err) {
      setMessage(err?.message || 'Failed to load mail templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) return;
    setForm({
      name: selected.name || '',
      description: selected.description || '',
      subject: selected.subject || '',
      body: selected.body || '',
      variables: selected.variables || [],
    });
  }, [selected]);

  const saveTemplate = async () => {
    const cid = companyId.trim();
    const query = cid
      ? `?cid=${encodeURIComponent(cid)}`
      : '?scope=global';
    setSaving(true);
    setMessage('');
    try {
      const response = await aiAutomationApi.put(
        `/admin/mail-templates/${encodeURIComponent(selectedKey)}${query}`,
        form,
      );
      const updated = response?.template;
      setTemplates((prev) => prev.map((item) => (
        item.template_key === selectedKey ? { ...item, ...updated } : item
      )));
      setMessage(cid ? 'Company template saved.' : 'Global template saved.');
    } catch (err) {
      setMessage(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!companyId.trim()) {
      setMessage('Enter a company id to reset its override.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const response = await aiAutomationApi.post(
        `/admin/mail-templates/${encodeURIComponent(selectedKey)}/reset?cid=${encodeURIComponent(companyId.trim())}`,
        {},
      );
      setTemplates(getVisibleTemplates(response?.templates || []));
      setMessage('Company override removed. Global template is active.');
    } catch (err) {
      setMessage(err?.message || 'Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950 px-6 py-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mail Templates</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Leave Company ID empty to edit global templates. Enter a Company ID to edit that company override.
          </p>
        </div>
        <button
          type="button"
          onClick={loadTemplates}
          disabled={loading}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"
        >
          Refresh
        </button>
      </div>

      <div className="mb-5 flex items-end gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company ID</span>
          <input
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
            placeholder="Leave empty to edit global"
            className="w-72 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
          />
        </label>
        <button
          type="button"
          onClick={loadTemplates}
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white"
        >
          Load
        </button>
      </div>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-800 dark:bg-blue-950/30 dark:border-blue-900 dark:text-blue-200">
          {message}
        </div>
      )}

      <div className="grid grid-cols-[320px_1fr] gap-5">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          {templates.map((template) => (
            <button
              type="button"
              key={template.template_key}
              onClick={() => setSelectedKey(template.template_key)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-800 ${
                selectedKey === template.template_key ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{template.name}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {template.is_custom ? 'Company' : 'Global'}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{template.template_key}</div>
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5">
          {selected ? (
            <>
              <div className="mb-4 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-300">
                Editing scope: <strong>{companyId.trim() ? `Company ${companyId.trim()}` : 'Global'}</strong>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</span>
                  <input
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Template Key</span>
                  <input
                    value={selected.template_key}
                    readOnly
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-500"
                  />
                </label>
              </div>

              <label className="block mb-4">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</span>
                <input
                  value={form.description}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-white"
                />
              </label>

              <label className="block mb-4">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</span>
                <input
                  value={form.subject}
                  onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-white"
                />
              </label>

              <label className="block mb-4">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Body</span>
                <textarea
                  value={form.body}
                  onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
                  rows={14}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-sm text-gray-900 dark:text-white font-mono"
                />
              </label>

              <div className="mb-5">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Variable Keys</div>
                <div className="flex flex-wrap gap-2">
                  {(form.variables || []).map((variable) => (
                    <span key={variable} className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300">
                      {`{{${variable}}}`}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={saveTemplate}
                  disabled={saving || !selectedKey}
                  className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (companyId.trim() ? 'Save Company Template' : 'Save Global Template')}
                </button>
                <button
                  type="button"
                  onClick={resetTemplate}
                  disabled={saving || !selectedKey || !companyId.trim()}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                >
                  Reset to Global
                </button>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">No templates found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
